import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import JobCard from '../components/JobCard';
import { API_URL, formatDate, getStatusBadgeClass, licenseOptions } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Bookmark,
  FileText,
  User,
  Settings,
  Loader2,
  Upload,
  MapPin,
  Phone,
  Calendar,
  Building2,
  ExternalLink,
  Award,
  GraduationCap,
  Trash2,
  FileCheck,
  Archive,
  MessageSquare,
  Sparkles,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import MessagesPanel from '../components/MessagesPanel';
import { useJobSeekerCtx } from './JobSeekerContext';
import JobSeekerLicenseVerification from './JobSeekerLicenseVerification';


const JobSeekerTabProfile = () => {
  const { applications, applyParsedDataToProfile, deleteReason, deleteStep, deletingAccount, handleDegreeUpload, handleDeleteAccount, handleDeleteDocument, handleLicenseUpload, handleParseResume, handleProfileSubmit, handleResumeUpload, parsedResume, parsingResume, profileData, saving, setDeleteReason, setDeleteStep, setProfileData, setShowDeleteConfirm, setShowParseResults, showDeleteConfirm, showParseResults, uploading, uploadingDegree, uploadingLicense, user } = useJobSeekerCtx();
  return (
    <>
            <JobSeekerLicenseVerification />
            <div className="bg-white rounded-xl p-6 border border-gray-100 mt-6">
              <h3 className="font-semibold text-gray-900 mb-6">Your Profile</h3>
              
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="mt-1"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="license_type">License Type</Label>
                    <Select
                      value={profileData.license_type}
                      onValueChange={(value) => setProfileData({ ...profileData, license_type: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="profile-license-select">
                        <SelectValue placeholder="Select license" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LCSW">LCSW</SelectItem>
                        <SelectItem value="LMSW">LMSW</SelectItem>
                        <SelectItem value="LBSW">LBSW</SelectItem>
                        <SelectItem value="SWA">Social Work Associate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="mt-1"
                      data-testid="profile-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={profileData.location}
                      onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                      placeholder="City, State"
                      className="mt-1"
                      data-testid="profile-location-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="years_experience">Years of Experience</Label>
                    <Input
                      id="years_experience"
                      type="number"
                      min="0"
                      value={profileData.years_experience}
                      onChange={(e) => setProfileData({ ...profileData, years_experience: e.target.value })}
                      className="mt-1"
                      data-testid="profile-experience-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="specializations">Specializations</Label>
                    <Input
                      id="specializations"
                      value={profileData.specializations}
                      onChange={(e) => setProfileData({ ...profileData, specializations: e.target.value })}
                      placeholder="Mental Health, Pediatrics, etc."
                      className="mt-1"
                      data-testid="profile-specializations-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    value={profileData.bio}
                    onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                    placeholder="Tell employers about yourself..."
                    rows={4}
                    className="mt-1"
                    data-testid="profile-bio-input"
                  />
                </div>

                {/* Resume Upload */}
                <div className="border-t pt-6">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-primary" />
                    Your Documents
                  </h4>
                  <p className="text-sm text-gray-500 mb-4">
                    Upload your documents once and they'll be automatically included with every application.
                  </p>
                  
                  <div className="space-y-4">
                    {/* Resume */}
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Resume</p>
                          {user?.resume_url ? (
                            <p className="text-sm text-emerald-600">{user.resume_url}</p>
                          ) : (
                            <p className="text-sm text-gray-500">Required for applications</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {user?.resume_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDocument('resume')}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleResumeUpload}
                            className="hidden"
                            data-testid="resume-upload-input"
                          />
                          <div className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                            {uploading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4" />
                            )}
                            <span className="text-sm">{uploading ? 'Uploading...' : user?.resume_url ? 'Replace' : 'Upload'}</span>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* License */}
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <Award className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Professional License</p>
                          {user?.license_url ? (
                            <p className="text-sm text-emerald-600">{user.license_url}</p>
                          ) : (
                            <p className="text-sm text-gray-500">Required for applications</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {user?.license_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDocument('license')}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleLicenseUpload}
                            className="hidden"
                            data-testid="license-upload-input"
                          />
                          <div className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                            {uploadingLicense ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4" />
                            )}
                            <span className="text-sm">{uploadingLicense ? 'Uploading...' : user?.license_url ? 'Replace' : 'Upload'}</span>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Degree */}
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <GraduationCap className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Degree/Diploma</p>
                          {user?.degree_url ? (
                            <p className="text-sm text-emerald-600">{user.degree_url}</p>
                          ) : (
                            <p className="text-sm text-gray-500">Optional</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {user?.degree_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDocument('degree')}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleDegreeUpload}
                            className="hidden"
                            data-testid="degree-upload-input"
                          />
                          <div className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                            {uploadingDegree ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4" />
                            )}
                            <span className="text-sm">{uploadingDegree ? 'Uploading...' : user?.degree_url ? 'Replace' : 'Upload'}</span>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">Accepted formats: PDF, DOC, DOCX, JPG, PNG (max 5MB)</p>
                </div>

                {/* AI Resume Parser Section */}
                <div className="border-t pt-6">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-secondary" />
                    AI Resume Parser
                    <span className="text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded-full">Beta</span>
                  </h4>
                  <p className="text-sm text-gray-500 mb-4">
                    Upload your resume and let AI extract your information to auto-fill your profile.
                  </p>
                  
                  <div className="flex items-center gap-4">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleParseResume}
                        className="hidden"
                        data-testid="ai-parse-resume-input"
                      />
                      <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-secondary to-secondary-hover text-white rounded-lg hover:shadow-md transition-all">
                        {parsingResume ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Analyzing...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            <span>Parse Resume with AI</span>
                          </>
                        )}
                      </div>
                    </label>
                  </div>

                  {/* Parsed Results */}
                  {showParseResults && parsedResume && (
                    <div className="mt-4 p-4 bg-secondary/5 border border-secondary/20 rounded-xl">
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          Extracted Information
                        </h5>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowParseResults(false)}
                          className="text-gray-500"
                        >
                          Dismiss
                        </Button>
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-4 mb-4">
                        {parsedResume.name && (
                          <div>
                            <p className="text-xs text-gray-500">Name</p>
                            <p className="font-medium text-gray-900">{parsedResume.name}</p>
                          </div>
                        )}
                        {parsedResume.email && (
                          <div>
                            <p className="text-xs text-gray-500">Email</p>
                            <p className="font-medium text-gray-900">{parsedResume.email}</p>
                          </div>
                        )}
                        {parsedResume.phone && (
                          <div>
                            <p className="text-xs text-gray-500">Phone</p>
                            <p className="font-medium text-gray-900">{parsedResume.phone}</p>
                          </div>
                        )}
                        {parsedResume.location && (
                          <div>
                            <p className="text-xs text-gray-500">Location</p>
                            <p className="font-medium text-gray-900">{parsedResume.location}</p>
                          </div>
                        )}
                        {parsedResume.years_experience && (
                          <div>
                            <p className="text-xs text-gray-500">Years Experience</p>
                            <p className="font-medium text-gray-900">{parsedResume.years_experience}</p>
                          </div>
                        )}
                      </div>
                      
                      {parsedResume.skills && parsedResume.skills.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs text-gray-500 mb-2">Skills</p>
                          <div className="flex flex-wrap gap-2">
                            {parsedResume.skills.slice(0, 8).map((skill, i) => (
                              <span key={i} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {parsedResume.summary && (
                        <div className="mb-4">
                          <p className="text-xs text-gray-500 mb-1">Summary</p>
                          <p className="text-sm text-gray-700 line-clamp-3">{parsedResume.summary}</p>
                        </div>
                      )}
                      
                      <Button
                        onClick={applyParsedDataToProfile}
                        className="bg-secondary hover:bg-secondary-hover text-white"
                        data-testid="apply-parsed-data-btn"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Apply to My Profile
                      </Button>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-primary hover:bg-primary-hover text-white"
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Profile'
                  )}
                </Button>
              </form>

              {/* Delete Account Section */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Delete Account</h3>
                
                {!showDeleteConfirm ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="border-gray-300 text-gray-600 hover:bg-gray-50"
                    data-testid="delete-account-btn"
                  >
                    Delete My Account
                  </Button>
                ) : deleteStep === 'confirm' ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-800 font-medium mb-4">
                      Are you sure you want to delete your account?
                    </p>
                    <div className="flex gap-3">
                      <Button
                        variant="destructive"
                        onClick={() => setDeleteStep('reason')}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Yes, Delete
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteStep('confirm');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-800 font-medium mb-3">
                      We're sorry to see you go. Why are you leaving?
                    </p>
                    <Textarea
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      placeholder="Your feedback helps us improve (optional)"
                      className="mb-4"
                      rows={3}
                    />
                    <div className="flex gap-3">
                      <Button
                        variant="destructive"
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="confirm-delete-btn"
                      >
                        {deletingAccount ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          'Delete Account'
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteStep('confirm');
                          setDeleteReason('');
                        }}
                        disabled={deletingAccount}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
    </>
  );
};

export default JobSeekerTabProfile;
